import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  setPersistence,
  signInWithEmailAndPassword,
  browserLocalPersistence,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";

import { auth, db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { setPageTitle } from "../components/DocumentTitle";
import { applyWaitlistApprovalToNewUser } from "../services/adminApprovalService";
import { initializeUserPerks } from "../services/userPerksService";
import { reserveUsername } from "../services/usernameService";
import { resolveReferralCode } from "../services/referralService";
import { persistSignupReferralCode, writeReferralIntent } from "../services/referralIntentService";
import { markA2hsPendingAfterSignup } from "../utils/a2hs";
import { fetchUserByHandle } from "../services/userLookupService";
import { getNicheMeta, NICHE_OPTIONS } from "../components/niche";
import { hasCreatorAccess } from "../utils/roleUtils";
import { formatCount } from "../utils/formatCount";
import "./ReferralLanding.css";

const FALLBACK_BG = {
  fan: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=700&q=80",
  creator: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=700&q=80",
  creatorWork: "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=700&q=80",
};

const FALLBACK_AVATAR = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=80";

function usernameFromSignup(name, email) {
  const fromName = name.trim().toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20);
  const fromEmail = email.split("@")[0].toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20);
  return fromName || fromEmail || "user";
}

function firstNameOf(user) {
  const raw = String(user?.displayName || user?.username || user?.handle || "Someone").trim();
  return raw.split(/\s+/)[0] || "Someone";
}

function referrerIsCreator(user) {
  return hasCreatorAccess(user) || user?.role === "creator" || user?.isCreator === true;
}

async function resolveReferrer(code) {
  const trimmed = String(code || "").trim();
  if (!trimmed) return null;
  const uid = await resolveReferralCode(trimmed);
  if (uid) {
    const snap = await getDoc(doc(db, "users", uid));
    if (snap.exists()) return { uid: snap.id, ...snap.data() };
  }
  try {
    return await fetchUserByHandle(trimmed);
  } catch {
    return null;
  }
}

function scenarioCopy({ referrer, joiningAsCreator }) {
  const fromCreator = referrerIsCreator(referrer);
  const name = firstNameOf(referrer);
  const fullName = referrer?.displayName || name;
  const nicheMeta = getNicheMeta(referrer?.niche);
  const avatar = referrer?.avatarUrl || referrer?.avatar || FALLBACK_AVATAR;
  const bg = referrer?.bannerImage || (fromCreator ? FALLBACK_BG.creatorWork : joiningAsCreator ? FALLBACK_BG.creator : FALLBACK_BG.fan);

  if (!referrer) {
    return {
      kicker: joiningAsCreator ? "✦ You were invited to create" : "✦ You were invited",
      headline: joiningAsCreator ? <>Someone thinks you should <em>post here too.</em></> : <>Someone wants you to <em>check this out.</em></>,
      sub: joiningAsCreator
        ? "No algorithm, no fighting for reach — post a drop, the first 100 people find it, you get paid. Canadian creators only, fans worldwide welcome."
        : "100ViewsOnly is Canada's creator platform — the first 100 people to see a drop get in free, then it's gone. Join free, browse everything, no card needed.",
      aside: joiningAsCreator ? "no algorithm, no gatekeeping — just post" : "yes, this is a real invite from a real person",
      autofollow: false,
      avatar,
      bg,
      nicheLabel: null,
      stats: null,
    };
  }

  if (!fromCreator && !joiningAsCreator) {
    return {
      kicker: "✦ You were invited",
      headline: <>{name} wants you to <em>check this out.</em></>,
      sub: "100ViewsOnly is Canada's creator platform — the first 100 people to see a drop get in free, then it's gone. Join free, browse everything, no card needed.",
      aside: "yes, this is a real invite from a real person",
      autofollow: false,
      avatar,
      bg,
      nicheLabel: null,
      stats: null,
    };
  }

  if (!fromCreator && joiningAsCreator) {
    return {
      kicker: "✦ You were invited to create",
      headline: <>{name} thinks you should <em>post here too.</em></>,
      sub: "No algorithm, no fighting for reach — post a drop, the first 100 people find it, you get paid. Canadian creators only, fans worldwide welcome.",
      aside: "no algorithm, no gatekeeping — just post",
      autofollow: false,
      avatar,
      bg,
      nicheLabel: null,
      stats: null,
    };
  }

  if (fromCreator && joiningAsCreator) {
    return {
      kicker: "✦ You were invited to create",
      headline: <>{fullName} invited you to <em>post your own drops.</em></>,
      sub: `${name} is posting whatever they want — no algorithm deciding who sees it. Canadian creators only, fans worldwide welcome.`,
      aside: "they're real, their earnings are real, so is this invite",
      autofollow: false,
      avatar,
      bg,
      nicheLabel: nicheMeta?.label || null,
      stats: {
        subscribers: referrer.supporterCount || referrer.followerCount || 0,
        drops: referrer.totalPosts || 0,
      },
    };
  }

  return {
    kicker: "✦ You were invited",
    headline: <>{fullName} wants you to <em>see their work.</em></>,
    sub: `${nicheMeta?.label ? `${nicheMeta.label}. ` : ""}The first 100 people to see each drop get it free — after that it's gone.`,
    aside: "first 100 viewers get in free — always",
    autofollow: true,
    followName: fullName,
    avatar,
    bg,
    nicheLabel: nicheMeta?.label || null,
    stats: {
      subscribers: referrer.supporterCount || referrer.followerCount || 0,
      drops: referrer.totalPosts || 0,
    },
  };
}

export default function ReferralLanding({ joinAs: joinAsProp } = {}) {
  const { code: routeCode } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, refreshUserProfile, refreshUserPerks } = useAuth();

  const joiningAsCreator =
    joinAsProp === "creator" ||
    searchParams.get("as") === "creator" ||
    location.pathname === "/creators/join";

  const refCode = (routeCode || searchParams.get("ref") || "").trim();

  const [referrer, setReferrer] = useState(null);
  const [tab, setTab] = useState("signup");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [niche, setNiche] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPageTitle("100ViewsOnly — You're invited");
  }, []);

  useEffect(() => {
    if (refCode) persistSignupReferralCode(refCode);
  }, [refCode]);

  useEffect(() => {
    let cancelled = false;
    if (!refCode) {
      setReferrer(null);
      return undefined;
    }
    resolveReferrer(refCode).then((user) => {
      if (!cancelled) setReferrer(user);
    });
    return () => { cancelled = true; };
  }, [refCode]);

  const copy = useMemo(
    () => scenarioCopy({ referrer, joiningAsCreator }),
    [referrer, joiningAsCreator]
  );

  const finishAuth = () => navigate("/", { replace: true });
  const finishSignup = () => {
    markA2hsPendingAfterSignup();
    finishAuth();
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      await setPersistence(auth, browserLocalPersistence);
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      await refreshUserProfile?.(credential.user.uid);
      finishAuth();
    } catch {
      setError("Incorrect email or password. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!displayName.trim() || !email.trim() || !password.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    if (password.length < 6) {
      setError("Password should be at least 6 characters.");
      return;
    }
    if (joiningAsCreator && !niche) {
      setError("Please select a niche for your creator account.");
      return;
    }

    const signupEmail = email.trim();
    const cleanUsername = usernameFromSignup(displayName, signupEmail);

    try {
      setLoading(true);
      setError("");
      await setPersistence(auth, browserLocalPersistence);
      if (refCode) {
        try {
          await writeReferralIntent({ email: signupEmail, code: refCode });
        } catch (refErr) {
          console.warn("Referral intent (pre-auth) error:", refErr);
        }
      }

      const result = await createUserWithEmailAndPassword(auth, signupEmail, password);
      const finalUsername = await reserveUsername({ username: cleanUsername, uid: result.user.uid });

      const profile = {
        uid: result.user.uid,
        email: result.user.email || signupEmail.toLowerCase(),
        displayName: displayName.trim(),
        username: finalUsername,
        handle: finalUsername,
        bio: "",
        avatar: "",
        bannerImage: "",
        theme: "warmCream",
        isCreator: joiningAsCreator,
        role: joiningAsCreator ? "creator" : "fan",
        approved: false,
        creatorOnboardingComplete: false,
        followerCount: 0,
        supporterCount: 0,
        totalPosts: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(joiningAsCreator && niche ? { niche: [niche] } : {}),
      };

      await setDoc(doc(db, "users", result.user.uid), profile, { merge: true });
      await applyWaitlistApprovalToNewUser(result.user.uid, profile.email);
      await initializeUserPerks(result.user.uid);

      try {
        await sendEmailVerification(result.user);
      } catch (verificationError) {
        console.warn("Verification email warning:", verificationError);
      }

      if (refCode) {
        try {
          await writeReferralIntent({
            uid: result.user.uid,
            email: result.user.email || signupEmail,
            code: refCode,
          });
        } catch (refErr) {
          console.warn("Referral intent error:", refErr);
        }
      }

      await refreshUserProfile?.(result.user.uid);
      await refreshUserPerks?.(result.user.uid);
      finishSignup();
    } catch (err) {
      setError(err.message || "Could not create account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rl-page">
      <div className="rl-logo-lockup">
        <svg width="34" height="24" viewBox="0 0 112 48" fill="none" aria-hidden>
          <path d="M0 24 C14 5 38 0 56 0 C74 0 98 5 112 24 C98 43 74 48 56 48 C38 48 14 43 0 24Z" stroke="#C4790A" strokeWidth="4" fill="none" />
          <circle cx="56" cy="24" r="13" fill="#FDF3E0" />
          <circle cx="56" cy="24" r="8" fill="#C4790A" />
          <circle cx="59.5" cy="20.5" r="2.5" fill="white" opacity="0.95" />
        </svg>
        <div className="rl-logo-wordmark">100<em>Views</em>Only</div>
      </div>

      <div className="rl-invite-card">
        <div className="rl-invite-bg">
          <img src={copy.bg} alt="" />
        </div>
        <div className="rl-invite-shade" />
        <div className="rl-invite-texture" />
        <div className="rl-invite-kicker">{copy.kicker}</div>
        <div className="rl-avatar-wrap">
          <div className="rl-avatar-ring" />
          <img className="rl-avatar" src={copy.avatar} alt="" />
        </div>
        <div className="rl-headline">{copy.headline}</div>
        <p className="rl-sub">{copy.sub}</p>
        {copy.nicheLabel && <span className="rl-niche-badge">{copy.nicheLabel}</span>}
        {copy.stats && (
          <div className="rl-stats">
            <div className="rl-stat">
              <div className="rl-stat-num">{formatCount(copy.stats.subscribers)}</div>
              <div className="rl-stat-lbl">Subscribers</div>
            </div>
            <div className="rl-stat">
              <div className="rl-stat-num">{formatCount(copy.stats.drops)}</div>
              <div className="rl-stat-lbl">Drops</div>
            </div>
          </div>
        )}
      </div>

      <span className="rl-aside">{copy.aside}</span>

      <div className="rl-signup-card">
        {currentUser ? (
          <div className="rl-already">
            You&apos;re already on 100ViewsOnly — this invite is for someone new.
            <br />
            <Link to="/">Go to Home</Link>
          </div>
        ) : (
          <>
            <div className="rl-tabs">
              <button type="button" className={`rl-tab${tab === "signup" ? " active" : ""}`} onClick={() => { setTab("signup"); setError(""); }}>
                Create Account
              </button>
              <button type="button" className={`rl-tab${tab === "signin" ? " active" : ""}`} onClick={() => { setTab("signin"); setError(""); }}>
                Sign In
              </button>
            </div>

            {tab === "signup" && copy.autofollow && copy.followName && (
              <div className="rl-autofollow">
                <span aria-hidden>👋 </span>
                You&apos;ll automatically follow <strong>{copy.followName}</strong> when you sign up — you can unfollow anytime.
              </div>
            )}

            {error && <p className="rl-error">{error}</p>}

            <form onSubmit={tab === "signup" ? handleSignUp : handleSignIn}>
              {tab === "signup" && (
                <div className="rl-field">
                  <label className="rl-field-label" htmlFor="rl-name">Your Name</label>
                  <input id="rl-name" className="rl-field-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Jane Smith" autoComplete="name" />
                </div>
              )}
              <div className="rl-field">
                <label className="rl-field-label" htmlFor="rl-email">Email Address</label>
                <input id="rl-email" className="rl-field-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" autoComplete={tab === "signup" ? "email" : "username"} />
              </div>
              <div className="rl-field">
                <label className="rl-field-label" htmlFor="rl-password">Password</label>
                <input id="rl-password" className="rl-field-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={tab === "signup" ? "At least 8 characters" : ""} autoComplete={tab === "signup" ? "new-password" : "current-password"} />
              </div>
              {tab === "signup" && joiningAsCreator && (
                <div className="rl-field">
                  <label className="rl-field-label" htmlFor="rl-niche">Your niche</label>
                  <select id="rl-niche" className="rl-field-input" value={niche} onChange={(e) => setNiche(e.target.value)}>
                    <option value="">Select a niche</option>
                    {NICHE_OPTIONS.map((opt) => (
                      <option key={opt.key || opt.id || opt.value} value={opt.key || opt.id || opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="rl-btn-wrap">
                <div className="rl-btn-glow" />
                <button type="submit" className="rl-btn-primary" disabled={loading}>
                  {loading
                    ? "Please wait…"
                    : tab === "signin"
                      ? "Sign In"
                      : joiningAsCreator
                        ? "Continue as a Creator"
                        : "Join Free"}
                </button>
              </div>
            </form>

            {tab === "signup" ? (
              <p className="rl-legal">
                By joining you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>
              </p>
            ) : (
              <p className="rl-legal">Already a member — signing in won&apos;t count as a new referral.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
